import React from 'react';
import dayjs from '@shared/lib/dayjs';
import { Controller, type Control } from 'react-hook-form';
import { AppAdminFormLabel, AppCheckbox, AppForm, AppTextField } from '@/components/atoms';
import RA2NDatePicker from '@/components/organisms/RA2NDatePicker/RA2NDatePicker';
import { dateValueToIso } from '@/utils/dateConverter';
import type { MassagerEditVisitFormData } from '@/schemas/massagerEditVisitSchema';

interface VisitSuddenFieldsProps {
  control: Control<MassagerEditVisitFormData>;
  /** Whether the checkbox itself should render. Locked-grandfather rule lives in the page. */
  visible: boolean;
  /** Whether the 4 conditional fields should render (driven by watch('is_sudden')). */
  expanded: boolean;
  /** Locked → checkbox + fields disabled. */
  disabled: boolean;
  /**
   * Whether THIS field is the page-level "first error". The page owns the
   * unified banner + single-red-border state; each input here only renders
   * red when its key is the current first error.
   */
  isFirstError: (name: keyof MassagerEditVisitFormData) => boolean;
  /**
   * Clears RHF errors for the 4 sudden fields as a group. Called from every
   * sudden input's onChange so the shared banner disappears as soon as the
   * user starts engaging with this section.
   */
  clearSuddenErrors: () => void;
  /** Bubbles up the 連携日 calendar open state so the parent can reserve scroll
   *  space (e.g. bottom padding) while the popup is visible. */
  onCoordinationOpenChange?: (open: boolean) => void;
}

/**
 * 突発的な往療 section: the checkbox + the 4 conditional fields (医療機関名,
 * 医師名, 突発往療理由, 連携日) shown when ticked. A single banner replaces
 * per-field help text when any of the 4 fail validation, matching AddVisitCard.
 */
const VisitSuddenFields: React.FC<VisitSuddenFieldsProps> = ({
  control,
  visible,
  expanded,
  disabled,
  isFirstError,
  clearSuddenErrors,
  onCoordinationOpenChange,
}) => {
  if (!visible) return null;
  return (
    <>
      <Controller
        name="is_sudden"
        control={control}
        render={({ field }) => (
          <AppCheckbox
            checked={!!field.value}
            onChange={e => field.onChange(e.target.checked)}
            disabled={disabled}
            className="self-start"
          >
            突発的な往療
          </AppCheckbox>
        )}
      />

      {expanded && (
        <div className="flex flex-col gap-3 rounded-md bg-gray-50 p-3">
          <Controller
            name="medical_institution_name"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <AppAdminFormLabel label="医療機関名" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('medical_institution_name') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <AppTextField
                    id="medical-institution-name"
                    value={field.value}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    onChange={e => {
                      field.onChange(e.target.value.slice(0, 50));
                      clearSuddenErrors();
                    }}
                    maxLength={50}
                    disabled={disabled}
                  />
                </AppForm.Item>
              </div>
            )}
          />
          <Controller
            name="doctor_name"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <AppAdminFormLabel label="医師名" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('doctor_name') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <AppTextField
                    id="doctor-name"
                    value={field.value}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    onChange={e => {
                      field.onChange(e.target.value.slice(0, 50));
                      clearSuddenErrors();
                    }}
                    maxLength={50}
                    disabled={disabled}
                  />
                </AppForm.Item>
              </div>
            )}
          />
          <Controller
            name="sudden_reason"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <AppAdminFormLabel label="突発往療理由" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('sudden_reason') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <AppTextField
                    id="sudden-reason"
                    value={field.value}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    onChange={e => {
                      field.onChange(e.target.value.slice(0, 50));
                      clearSuddenErrors();
                    }}
                    maxLength={50}
                    disabled={disabled}
                  />
                </AppForm.Item>
              </div>
            )}
          />
          <Controller
            name="coordination_date"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1">
                <AppAdminFormLabel label="連携日" required className="!mb-0" />
                <AppForm.Item
                  validateStatus={isFirstError('coordination_date') ? 'error' : undefined}
                  className="!mb-0"
                >
                  <RA2NDatePicker
                    value={field.value ? field.value.format('YYYY-MM-DD') : ''}
                    onChange={v => {
                      const iso = dateValueToIso(v);
                      field.onChange(iso ? dayjs(iso) : null);
                      clearSuddenErrors();
                    }}
                    className="w-full"
                    error={isFirstError('coordination_date')}
                    disabled={disabled}
                    placeholder="元号00年00月00日"
                    onOpenChange={onCoordinationOpenChange}
                  />
                </AppForm.Item>
              </div>
            )}
          />
          <p className="text-sm text-red-500">
            同意書交付の医師以外の場合、その理由をレセプト摘要欄に手書きで記載ください。
          </p>
        </div>
      )}
    </>
  );
};

export default VisitSuddenFields;
